import React, { useState } from "react";
import { useDispatch } from "react-redux";

import http from "../../helpers/http";
import { doObjToFormData } from "../../helpers/helpers";
import {
  CANCEL_SUBSCRIPTION_MESSAGE_FAILED,
  CANCEL_SUBSCRIPTION_MESSAGE_SUCCESS,
} from "./actionTypes";
import {
  CANCELLATION_REASONS,
  MAX_COMMENT,
  MAX_OTHER_TEXT,
  MIN_OTHER_TEXT,
  OTHER,
  findReason,
  isMeaningfulText,
} from "./cancellationReasons";

const popupStyle = {
  position: "fixed",
  top: "0",
  left: "0",
  width: "100%",
  height: "100%",
  background: "rgba(0, 0, 0, 0.7)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: "999",
};

const contentStyle = {
  position: "relative",
  background: "#fff",
  borderRadius: "5px",
  width: "90%",
  maxWidth: "600px",
  maxHeight: "90vh",
  overflowY: "auto",
  padding: "44px 36px 32px",
  boxShadow: "0 5px 10px rgba(0, 0, 0, 0.3)",
};

const fieldsetStyle = {
  border: 0,
  margin: "0 0 20px",
  padding: 0,
};

const legendStyle = {
  fontSize: "16px",
  fontWeight: 600,
  paddingBottom: "10px",
  marginBottom: "16px",
};

const radioStyle = {
  display: "flex",
  alignItems: "center",
  marginBottom: "10px",
};

const labelStyle = {
  marginLeft: "10px",
};

const textLabelStyle = {
  display: "block",
  fontSize: "14px",
  marginBottom: "6px",
};

const textareaStyle = {
  display: "block",
  width: "100%",
  height: "90px",
  padding: "10px 12px",
  fontSize: "16px",
  borderRadius: "5px",
  border: "1px solid #ccc",
  marginBottom: "20px",
};

const hintStyle = {
  fontSize: "13px",
  color: "#667085",
  margin: "-2px 0 8px",
};

const errorStyle = {
  color: "#b42318",
  fontSize: "14px",
  marginBottom: "12px",
};

const actionRowStyle = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap",
  marginTop: "8px",
};

// site_btn's own side padding is sized for page CTAs; trimmed here so Back,
// Keep Subscription and Cancel subscription sit on one line on desktop.
const actionButtonStyle = {
  padding: "0 18px",
};

// Back sits on the left, away from the two decisions on the right.
const backButtonStyle = {
  ...actionButtonStyle,
  marginRight: "auto",
};

// The server sends dates as YYYY-MM-DD in UTC, taken from Stripe. Format them
// in UTC too, so a browser west of UTC does not show the day before.
export const formatCancellationDate = (value) => {
  if (!value) return "";
  const d = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const CustomPopup = ({ onCancel }) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState("reason");
  const [mainReason, setMainReason] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [otherText, setOtherText] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const reason = findReason(mainReason);
  const hasFollowUpList = reason?.followUps.length > 0;
  const needsOtherText =
    mainReason === OTHER || (hasFollowUpList && followUp === OTHER);

  const handleMainReasonChange = (e) => {
    const next = e.target.value;
    if (next !== mainReason) {
      // A follow up only makes sense under the reason it was asked for.
      setFollowUp("");
      setOtherText("");
    }
    setMainReason(next);
    setError("");
  };

  const handleFollowUpChange = (e) => {
    setFollowUp(e.target.value);
    setError("");
  };

  const handleContinue = (e) => {
    e.preventDefault();
    if (!mainReason) {
      setError("Please choose the main reason you are canceling.");
      return;
    }
    setError("");
    setStep("follow_up");
  };

  const handleBack = () => {
    setError("");
    setStep("reason");
  };

  const validate = () => {
    if (hasFollowUpList && !followUp) {
      return "Please answer the question above.";
    }
    if (needsOtherText && !isMeaningfulText(otherText)) {
      return `Please tell us briefly what happened (at least ${MIN_OTHER_TEXT} characters).`;
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }

    setSubmitting(true);
    setError("");

    const ref = {
      main_reason: mainReason,
      follow_up: hasFollowUpList ? followUp : "",
      other_text: needsOtherText ? otherText.trim() : "",
      comment: comment.trim(),
    };

    try {
      const { data } = await http.post(
        "cancel-member-subscription",
        doObjToFormData({
          token: localStorage.getItem("authToken"),
          ref,
        })
      );

      if (Number(data?.status) === 1) {
        dispatch({ type: CANCEL_SUBSCRIPTION_MESSAGE_SUCCESS, payload: data });
        setConfirmation(data?.cancellation || {});
        setStep("done");
        return;
      }

      dispatch({ type: CANCEL_SUBSCRIPTION_MESSAGE_FAILED, payload: null });
      setError(
        data?.msg ||
          "We could not cancel your subscription. Please try again or contact support."
      );
    } catch (err) {
      dispatch({ type: CANCEL_SUBSCRIPTION_MESSAGE_FAILED, payload: err });
      setError(
        "We could not reach the server, so your subscription has not been cancelled yet. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    // Reload so account settings and pricing show the cancelling state.
    window.location.reload();
  };

  const errorMessage = error ? (
    <div role="alert" id="cancel-popup-error" style={errorStyle}>
      {error}
    </div>
  ) : null;

  return (
    <div style={popupStyle}>
      <div
        style={contentStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-popup-title"
      >
        {step === "reason" && (
          <form onSubmit={handleContinue} noValidate>
            <h5 id="cancel-popup-title" style={{ marginTop: 0, marginBottom: 14 }}>
              Help us understand why you are leaving
            </h5>
            <fieldset style={fieldsetStyle}>
              <legend style={legendStyle}>
                Select the main reason you are canceling Rhino Recon.
              </legend>
              {CANCELLATION_REASONS.map((option) => (
                <div key={option.code} style={radioStyle}>
                  <input
                    type="radio"
                    id={`cancel-reason-${option.code}`}
                    name="main_reason"
                    value={option.code}
                    checked={mainReason === option.code}
                    onChange={handleMainReasonChange}
                  />
                  <label htmlFor={`cancel-reason-${option.code}`} style={labelStyle}>
                    {option.label}
                  </label>
                </div>
              ))}
            </fieldset>
            {errorMessage}
            <div style={actionRowStyle}>
              <button type="button" className="site_btn blank" onClick={onCancel}>
                Keep Subscription
              </button>
              <button type="submit" className="site_btn">
                Continue
              </button>
            </div>
          </form>
        )}

        {step === "follow_up" && reason && (
          <form onSubmit={handleSubmit} noValidate>
            <h5 id="cancel-popup-title" style={{ marginTop: 0, marginBottom: 14 }}>
              {reason.label}
            </h5>

            {hasFollowUpList ? (
              <fieldset style={fieldsetStyle}>
                <legend style={legendStyle}>{reason.question}</legend>
                {reason.followUps.map((option) => (
                  <div key={option.code} style={radioStyle}>
                    <input
                      type="radio"
                      id={`cancel-follow-up-${option.code}`}
                      name="follow_up"
                      value={option.code}
                      checked={followUp === option.code}
                      onChange={handleFollowUpChange}
                    />
                    <label
                      htmlFor={`cancel-follow-up-${option.code}`}
                      style={labelStyle}
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </fieldset>
            ) : null}

            {needsOtherText && (
              <>
                <label htmlFor="cancel-other-text" style={textLabelStyle}>
                  {hasFollowUpList
                    ? "Please tell us briefly what happened."
                    : reason.question}{" "}
                  (required)
                </label>
                <textarea
                  id="cancel-other-text"
                  value={otherText}
                  maxLength={MAX_OTHER_TEXT}
                  required
                  aria-describedby={error ? "cancel-popup-error" : undefined}
                  // The forms use noValidate, so mark the field invalid ourselves
                  // when the error showing is about this text.
                  aria-invalid={error && !isMeaningfulText(otherText) ? "true" : undefined}
                  onChange={(e) => {
                    setOtherText(e.target.value);
                    setError("");
                  }}
                  style={textareaStyle}
                />
              </>
            )}

            <label htmlFor="cancel-comment" style={textLabelStyle}>
              Anything else you would like us to know? (optional)
            </label>
            {reason.commentPrompt ? (
              <p id="cancel-comment-hint" style={hintStyle}>
                {reason.commentPrompt}
              </p>
            ) : null}
            <textarea
              id="cancel-comment"
              value={comment}
              maxLength={MAX_COMMENT}
              aria-describedby={reason.commentPrompt ? "cancel-comment-hint" : undefined}
              onChange={(e) => setComment(e.target.value)}
              style={textareaStyle}
            />

            {errorMessage}
            <div style={actionRowStyle}>
              <button
                type="button"
                className="site_btn blank"
                style={backButtonStyle}
                onClick={handleBack}
                disabled={submitting}
              >
                Back
              </button>
              <button
                type="button"
                className="site_btn blank"
                style={actionButtonStyle}
                onClick={onCancel}
                disabled={submitting}
              >
                Keep Subscription
              </button>
              <button
                type="submit"
                className="site_btn"
                style={actionButtonStyle}
                disabled={submitting}
              >
                {submitting ? "Canceling…" : "Cancel subscription"}
              </button>
            </div>
          </form>
        )}

        {step === "done" && (
          <div role="status">
            <h5 id="cancel-popup-title" style={{ marginTop: 0, marginBottom: 14 }}>
              Your cancellation is confirmed
            </h5>
            <p style={{ marginBottom: 12, fontSize: 14, lineHeight: 1.5 }}>
              Your subscription will not renew, and you won't be charged again.
            </p>
            {confirmation?.effective_date || confirmation?.access_ends_on ? (
              <ul style={{ marginBottom: 16, fontSize: 14, lineHeight: 1.7 }}>
                {confirmation?.effective_date && (
                  <li>
                    Cancellation takes effect on{" "}
                    <strong>{formatCancellationDate(confirmation.effective_date)}</strong>.
                  </li>
                )}
                {confirmation?.access_ends_on && (
                  <li>
                    You keep access to your plan until{" "}
                    <strong>{formatCancellationDate(confirmation.access_ends_on)}</strong>.
                  </li>
                )}
              </ul>
            ) : null}
            <p style={{ marginBottom: 16, fontSize: 14, lineHeight: 1.5 }}>
              Thank you for your feedback.
            </p>
            <div style={actionRowStyle}>
              <button type="button" className="site_btn" onClick={handleDone}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomPopup;
